import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import salesOrdersService from "../../../services/legacy/salesOrdersService";
import customersService from "../../../services/legacy/customersService";
import salesOrderLinesService from "../../../services/legacy/salesOrderLinesService";
import itemsService from "../../../services/legacy/itemsService";
import cmtSeleccionBobinasService from "../../../services/legacy/cmtSeleccionBobinasService";
import custInvoiceTransService from "../../../services/legacy/custInvoiceTransService";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import CandidateRow from "./CandidateRow";
import LegendTab from "./LegendTab";
import {
  getFieldLoose,
  estadoPvBadgeStyle,
  formatDateOnly,
  formatNumber,
  isTrueFlag,
  toNumberOrNull,
  NOT_SYNCED_BADGE_STYLE,
  NOT_SYNCED_LABEL,
} from "./seleccionBobinasUtils";

const ESTADO_LABEL_KEYS = {
  0: "axEnums.CMTSeleccionBobinasEstadoPV.0",
  1: "axEnums.CMTSeleccionBobinasEstadoPV.1",
  2: "axEnums.CMTSeleccionBobinasEstadoPV.2",
  3: "axEnums.CMTSeleccionBobinasEstadoPV.3",
};

const selectionKey = (itemId, batchOrTransId) => `${String(itemId || "").toUpperCase()}::${String(batchOrTransId || "").toUpperCase()}`;

const candidateKey = (candidate) => selectionKey(candidate.itemId, candidate.inventBatchId || candidate.inventTransId);

const selectionRowKey = (row) => selectionKey(row.bobinaitemid, row.bobinainventbatchid || row.bobinainventtransid);

/** Mirrors (approximately) the backend's getKilosSeleccionados, client-side,
 * from the candidates + persisted selection rows already loaded for a line.
 * Also returns, per selection recId, the quantity it is currently
 * contributing - used as the "lineRemainingQty" basis when the user edits
 * the delegacion split for that row. */
const computeLineSelection = (orderedQty, candidates, selections) => {
  const candidateByKey = new Map(candidates.map((c) => [candidateKey(c), c]));
  let total = 0;
  const contributionByRecId = {};
  const ordered = Number(orderedQty) || 0;
  for (const row of selections) {
    const completa = isTrueFlag(row.selcompleta);
    const resto = isTrueFlag(row.selresto);
    const parcial = isTrueFlag(row.selparcial);
    if (!completa && !resto && !parcial) continue;
    const candidate = candidateByKey.get(selectionRowKey(row));
    const candidateQty = Number(candidate?.qtyAvailable) || 0;
    let contribution = 0;
    if (completa || resto) {
      contribution = candidateQty;
    } else {
      const stillNeeded = ordered - total;
      contribution = stillNeeded > 0 ? Math.min(stillNeeded, candidateQty) : 0;
    }
    total += contribution;
    if (row.recId) contributionByRecId[row.recId] = contribution;
  }
  return { totalKg: total, contributionByRecId };
};

const delegationSummary = (selections) => {
  const parts = [];
  selections.forEach((row) => {
    [1, 2, 3].forEach((i) => {
      const code = row[`delegacion${i}`];
      const pct = row[`pctdelegacion${i}`];
      if (code) parts.push(pct !== null && pct !== undefined ? `${code} (${formatNumber(pct, 1)}%)` : code);
    });
  });
  return parts.join(", ");
};

export default function SelectorBobinasPage() {
  const { t } = useTranslation();
  const { dataAreaId, id } = useParams();
  const iSalesId = id;

  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]);
  const [candidatesByLine, setCandidatesByLine] = useState({});
  const [selectionsByLine, setSelectionsByLine] = useState({});
  const [itemAttrCache, setItemAttrCache] = useState({});
  const [permissions, setPermissions] = useState({ canApprove: false });
  const [colorsDisabled, setColorsDisabled] = useState(false);
  const [topTab, setTopTab] = useState("order");
  const [bottomTab, setBottomTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [busyKeys, setBusyKeys] = useState(new Set());
  const [coverageConfirm, setCoverageConfirm] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastSales, setLastSales] = useState([]);
  const [lastSalesLoading, setLastSalesLoading] = useState(false);
  const [lastSalesError, setLastSalesError] = useState(false);
  const [lastSalesLoadedFor, setLastSalesLoadedFor] = useState(null);
  const nonceRef = useRef(0);

  const setBusy = useCallback((key, isBusy) => {
    setBusyKeys((prev) => {
      const next = new Set(prev);
      if (isBusy) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  const loadItemAttributes = useCallback(async (itemIds) => {
    const missing = itemIds.filter((itemId) => itemId && !(itemId in itemAttrCache));
    if (missing.length === 0) return;
    const entries = await Promise.all(missing.map(async (itemId) => {
      try {
        const page = await itemsService.getAll(0, 1, [], { itemId });
        const item = Array.isArray(page?.content) ? page.content[0] : null;
        if (!item) return [itemId, null];
        return [itemId, {
          itemname: getFieldLoose(item, "itemname"),
          gramaje: getFieldLoose(item, "inventgramajeitemid"),
          width: getFieldLoose(item, "width"),
          depth: getFieldLoose(item, "depth"),
        }];
      } catch {
        return [itemId, null];
      }
    }));
    setItemAttrCache((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  }, [itemAttrCache]);

  const loadCandidatesAndSelections = useCallback(async (linesList) => {
    const withRecId = linesList.filter((line) => getFieldLoose(line, "recId"));
    const results = await Promise.all(withRecId.map(async (line) => {
      const recId = getFieldLoose(line, "recId");
      try {
        const [candidates, selections] = await Promise.all([
          cmtSeleccionBobinasService.searchCandidates(recId),
          cmtSeleccionBobinasService.listBySalesLine(recId),
        ]);
        return [recId, candidates || [], selections || []];
      } catch (err) {
        console.error("Error loading candidates for line", recId, err);
        return [recId, [], []];
      }
    }));

    const candidatesMap = {};
    const selectionsMap = {};
    const allItemIds = new Set();
    results.forEach(([recId, candidates, selections]) => {
      candidatesMap[recId] = candidates;
      selectionsMap[recId] = selections;
      candidates.forEach((c) => allItemIds.add(c.itemId));
    });
    setCandidatesByLine(candidatesMap);
    setSelectionsByLine(selectionsMap);
    loadItemAttributes([...allItemIds]);
  }, [loadItemAttributes]);

  const refreshAll = useCallback(async () => {
    if (!iSalesId) return;
    setLoading(true);
    const myNonce = ++nonceRef.current;
    try {
      const orderFilters = { iSalesId };
      if (dataAreaId) orderFilters.dataAreaId = dataAreaId;
      const [orderResult, permissionsResult] = await Promise.all([
        salesOrdersService.getAll(0, 1, [], orderFilters),
        cmtSeleccionBobinasService.getPermissions().catch(() => ({ canApprove: false })),
      ]);
      if (nonceRef.current !== myNonce) return;
      const orderRow = Array.isArray(orderResult?.content) ? orderResult.content[0] : null;
      setOrder(orderRow);
      setPermissions(permissionsResult || { canApprove: false });

      const account = orderRow ? getFieldLoose(orderRow, "custaccount") : null;
      if (account) {
        customersService.getAll(0, 1, [], { accountNum: account }).then((page) => {
          if (nonceRef.current !== myNonce) return;
          setCustomer(Array.isArray(page?.content) ? page.content[0] : null);
        }).catch(() => setCustomer(null));
      }

      const effectiveDataAreaId = dataAreaId || (orderRow ? getFieldLoose(orderRow, "dataAreaId") : null);
      const linesResult = await salesOrderLinesService.getAll(0, 500, [], {
        iSalesId,
        ...(effectiveDataAreaId ? { dataAreaId: effectiveDataAreaId } : {}),
      });
      if (nonceRef.current !== myNonce) return;
      const linesList = Array.isArray(linesResult?.content) ? linesResult.content : [];
      setLines(linesList);
      await loadCandidatesAndSelections(linesList);
    } catch (err) {
      console.error("Error loading Selector de bobinas data:", err);
    } finally {
      if (nonceRef.current === myNonce) setLoading(false);
    }
  }, [dataAreaId, iSalesId, loadCandidatesAndSelections]);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAreaId, iSalesId]);

  const refreshLine = useCallback(async (recId) => {
    try {
      const [candidates, selections] = await Promise.all([
        cmtSeleccionBobinasService.searchCandidates(recId),
        cmtSeleccionBobinasService.listBySalesLine(recId),
      ]);
      setCandidatesByLine((prev) => ({ ...prev, [recId]: candidates || [] }));
      setSelectionsByLine((prev) => ({ ...prev, [recId]: selections || [] }));
      loadItemAttributes((candidates || []).map((c) => c.itemId));
    } catch (err) {
      console.error("Error refreshing line", recId, err);
    }
  }, [loadItemAttributes]);

  // "Ultimas Ventas" tab: lazily load the customer's posted invoice history
  // the first time the tab is opened (or when the order's customer/company
  // changes), via CustInvoiceTransController#lastSales. Mirrors the AX form's
  // CustInvoiceTrans datasource linkActive(), which resolves the customer via
  // salesTable.InvoiceAccount (falling back to CustAccount only when there is
  // no separate invoice account) rather than always using CustAccount - some
  // customers are invoiced under a different (parent/holding) account.
  const custAccountForSales = getFieldLoose(order, "invoiceaccount") || getFieldLoose(order, "custaccount");
  const dataAreaIdForSales = dataAreaId || getFieldLoose(order, "dataAreaId");

  useEffect(() => {
    if (topTab !== "sales") return;
    if (!custAccountForSales) return;
    const key = `${dataAreaIdForSales || ""}::${custAccountForSales}`;
    if (lastSalesLoadedFor === key) return;
    let cancelled = false;
    setLastSalesLoading(true);
    setLastSalesError(false);
    custInvoiceTransService.getLastSales(dataAreaIdForSales, custAccountForSales, 50)
      .then((rows) => {
        if (cancelled) return;
        setLastSales(Array.isArray(rows) ? rows : []);
        setLastSalesLoadedFor(key);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading customer last sales history", err);
        setLastSalesError(true);
      })
      .finally(() => {
        if (!cancelled) setLastSalesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTab, dataAreaIdForSales, custAccountForSales, lastSalesLoadedFor]);

  const canEditSelection = useMemo(() => {
    // Unknown/not-yet-synced state (null) is treated as editable (same as 0
    // "No enviado"), which is the safe default for a screen that has not
    // gone through the approval workflow yet.
    const estado = toNumberOrNull(getFieldLoose(order, "cmtseleccionbobinasestadopv")) ?? 0;
    return estado !== 2 || permissions.canApprove;
  }, [order, permissions]);

  const handleToggleFlag = useCallback(async (line, candidate, flagKey, checked) => {
    const recId = getFieldLoose(line, "recId");
    const key = `${recId}:${candidateKey(candidate)}`;
    setBusy(key, true);
    try {
      const isBatch = candidate.tipo === "DISPONIBLE_FISICO";
      const payload = {
        saleslinerecid: recId,
        tipo: candidate.tipo,
        bobinaitemid: candidate.itemId,
        bobinainventbatchid: isBatch ? candidate.inventBatchId : null,
        bobinainventtransid: !isBatch ? candidate.inventTransId : null,
        selcompleta: checked && flagKey === "selcompleta",
        selresto: checked && flagKey === "selresto",
        selparcial: checked && flagKey === "selparcial",
      };
      await cmtSeleccionBobinasService.toggleSelection(payload);
      await refreshLine(recId);
    } catch (err) {
      console.error("Error toggling selection:", err);
      window.alert(err?.message || t("seleccionBobinas.general.toggleError"));
    } finally {
      setBusy(key, false);
    }
  }, [refreshLine, setBusy, t]);

  const handleDelegacionSave = useCallback(async (line, existingSelection, payload, lineRemainingQty) => {
    if (!existingSelection?.recId) return;
    try {
      await cmtSeleccionBobinasService.setDelegacion(existingSelection.recId, {
        delegaciones: payload.delegaciones,
        percentages: payload.percentages,
        lineRemainingQty: lineRemainingQty || 0,
      });
      const recId = getFieldLoose(line, "recId");
      await refreshLine(recId);
    } catch (err) {
      console.error("Error saving delegacion:", err);
      window.alert(err?.message || t("seleccionBobinas.general.delegacionError"));
    }
  }, [refreshLine, t]);

  const doSendToManagement = useCallback(async () => {
    if (!iSalesId || sending) return;
    setSending(true);
    try {
      const result = await cmtSeleccionBobinasService.sendToManagement(iSalesId);
      window.alert(t("seleccionBobinas.actions.sendSuccess"));
      setOrder((prev) => (prev ? {
        ...prev,
        cmtseleccionbobinasestadopv: result?.cmtSeleccionBobinasEstadoPV,
        cmtseleccionbobinasenviadopor: result?.cmtSeleccionBobinasEnviadoPor,
      } : prev));
    } catch (err) {
      window.alert(err?.message || t("seleccionBobinas.actions.sendError"));
    } finally {
      setSending(false);
    }
  }, [iSalesId, sending, t]);

  const doMarkReviewed = useCallback(async () => {
    if (!iSalesId) return;
    try {
      await cmtSeleccionBobinasService.markReviewed(iSalesId);
      window.alert(t("seleccionBobinas.actions.markReviewedSuccess"));
      setCoverageConfirm(null);
      refreshAll();
    } catch (err) {
      window.alert(err?.message || t("seleccionBobinas.actions.markReviewedError"));
      setCoverageConfirm(null);
    }
  }, [iSalesId, refreshAll, t]);

  const handleMarkReviewedClick = useCallback(async () => {
    if (!iSalesId) return;
    try {
      const badLines = await cmtSeleccionBobinasService.checkCoverage(iSalesId);
      if (Array.isArray(badLines) && badLines.length > 0) {
        setCoverageConfirm(badLines);
      } else {
        doMarkReviewed();
      }
    } catch (err) {
      window.alert(err?.message || t("seleccionBobinas.actions.coverageCheckError"));
    }
  }, [iSalesId, doMarkReviewed, t]);

  // ---- derived data for rendering ----

  const lineComputations = useMemo(() => {
    const map = {};
    lines.forEach((line) => {
      const recId = getFieldLoose(line, "recId");
      const candidates = candidatesByLine[recId] || [];
      const selections = selectionsByLine[recId] || [];
      const orderedQty = getFieldLoose(line, "salesQty");
      const { totalKg, contributionByRecId } = computeLineSelection(orderedQty, candidates, selections);
      map[recId] = { candidates, selections, totalKg, contributionByRecId };
    });
    return map;
  }, [lines, candidatesByLine, selectionsByLine]);

  const estadoCodeRaw = toNumberOrNull(getFieldLoose(order, "cmtseleccionbobinasestadopv"));
  const estadoCode = estadoCodeRaw ?? 0;
  const blocked = customer ? toNumberOrNull(getFieldLoose(customer, "blocked")) || 0 : 0;

  const tabButton = (active, onClick, labelKey) => (
    <li className={active ? "active" : ""}>
      <button type="button" role="tab" className={`crud-form-tab-btn ${active ? "is-active" : ""}`} onClick={onClick}>
        {t(labelKey)}
      </button>
    </li>
  );

  return (
    <div className="page-container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>
          {t("seleccionBobinas.selector.title")} - {getFieldLoose(order, "salesId") || iSalesId}
          {" "}
          <span
            style={{
              display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
              ...(estadoCodeRaw === null ? NOT_SYNCED_BADGE_STYLE : estadoPvBadgeStyle(estadoCode)),
            }}
            title={estadoCodeRaw === null ? t("seleccionBobinas.notSyncedHint") : undefined}
          >
            {estadoCodeRaw === null ? NOT_SYNCED_LABEL : t(ESTADO_LABEL_KEYS[estadoCode] || ESTADO_LABEL_KEYS[0])}
          </span>
        </h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={refreshAll} disabled={loading}>
            {t("seleccionBobinas.selector.refresh")}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={doSendToManagement} disabled={sending}>
            {t("seleccionBobinas.actions.sendToManagement")}
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleMarkReviewedClick}
            disabled={!permissions.canApprove}
            title={!permissions.canApprove ? t("seleccionBobinas.actions.markReviewedNoPermission") : undefined}
          >
            {t("seleccionBobinas.actions.markReviewed")}
          </button>
        </div>
      </div>

      {loading && <p>{t("seleccionBobinas.selector.loading")}</p>}

      {/* Zona superior: datos del pedido */}
      <div className="crud-form-container" style={{ marginBottom: 16 }}>
        <div className="crud-form-tabs-nav-scroll" role="tablist">
          <ul className="nav nav-tabs crud-form-tabs-nav">
            {tabButton(topTab === "order", () => setTopTab("order"), "seleccionBobinas.topTabs.order")}
            {tabButton(topTab === "customer", () => setTopTab("customer"), "seleccionBobinas.topTabs.customerData")}
            {tabButton(topTab === "sales", () => setTopTab("sales"), "seleccionBobinas.topTabs.lastSales")}
          </ul>
        </div>
        <div style={{ maxHeight: 420, overflow: "auto", padding: 12 }}>
          {topTab === "order" && (
            <div>
              {blocked > 0 && (
                <div className="modern-alert alert-error" style={{ marginBottom: 12 }}>
                  {t("seleccionBobinas.order.blockedCustomerWarning")}
                </div>
              )}
              <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
                <div><strong>{t("seleccionBobinas.order.deliveryDate")}:</strong> {formatDateOnly(getFieldLoose(order, "requesteddelivdate"))}</div>
                <div><strong>{t("seleccionBobinas.order.creditLimit")}:</strong> {formatNumber(getFieldLoose(customer, "creditmax"))} <em style={{ fontSize: 11, color: "#9ca3af" }}>({t("seleccionBobinas.order.creditLimitNote")})</em></div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>{t("seleccionBobinas.order.columnQuality")}</th>
                      <th>{t("seleccionBobinas.order.columnItem")}</th>
                      <th>{t("seleccionBobinas.order.columnDescription")}</th>
                      <th>{t("seleccionBobinas.order.columnObservations")}</th>
                      <th>{t("seleccionBobinas.order.columnGramaje")}</th>
                      <th>{t("seleccionBobinas.order.columnWidth")}</th>
                      <th>{t("seleccionBobinas.order.columnDepth")}</th>
                      <th>{t("seleccionBobinas.order.columnOrderedQty")}</th>
                      <th>{t("seleccionBobinas.order.columnSelectedKg")}</th>
                      <th>{t("seleccionBobinas.order.columnDelegation")}</th>
                      <th>{t("seleccionBobinas.order.columnPrice")}</th>
                      <th>{t("seleccionBobinas.order.columnAmount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => {
                      const recId = getFieldLoose(line, "recId");
                      const calc = lineComputations[recId] || { totalKg: 0, selections: [] };
                      const calidad = toNumberOrNull(getFieldLoose(line, "cmtespecificacalidad"));
                      return (
                        <tr key={`${recId}-${getFieldLoose(line, "lineNum")}`}>
                          <td>{calidad === null ? NOT_SYNCED_LABEL : t(`axEnums.CMTEspecificaCalidad.${calidad}`)}</td>
                          <td>{getFieldLoose(line, "itemId")}</td>
                          <td>{getFieldLoose(line, "name")}</td>
                          <td>{getFieldLoose(line, "cmtobsselbobinas")}</td>
                          <td>{getFieldLoose(line, "gramajeitemid")}</td>
                          <td>{formatNumber(getFieldLoose(line, "width"))}</td>
                          <td>{formatNumber(getFieldLoose(line, "depth"))}</td>
                          <td>{formatNumber(getFieldLoose(line, "salesQty"))}</td>
                          <td>{formatNumber(calc.totalKg)}</td>
                          <td>{delegationSummary(calc.selections || [])}</td>
                          <td>{formatNumber(getFieldLoose(line, "salesPrice"))}</td>
                          <td>{formatNumber(getFieldLoose(line, "lineamount"))}</td>
                        </tr>
                      );
                    })}
                    {lines.length === 0 && (
                      <tr><td colSpan={12}>{t("seleccionBobinas.order.noLines")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {topTab === "customer" && (
            <table className="table table-striped" style={{ maxWidth: 640 }}>
              <tbody>
                <tr><th>{t("seleccionBobinas.customer.accountNum")}</th><td>{getFieldLoose(order, "custaccount")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.name")}</th><td>{getFieldLoose(customer, "name")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.namealias")}</th><td>{getFieldLoose(customer, "namealias")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.phone")}</th><td>{getFieldLoose(customer, "phone")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.group")}</th><td>{getFieldLoose(customer, "custgroup")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.paymentTerms")}</th><td>{getFieldLoose(customer, "paymtermid")}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.creditLimit")}</th><td>{formatNumber(getFieldLoose(customer, "creditmax"))}</td></tr>
                <tr><th>{t("seleccionBobinas.customer.blocked")}</th><td>{blocked > 0 ? t("common.yes") : t("common.no")}</td></tr>
              </tbody>
            </table>
          )}
          {topTab === "sales" && (
            <div>
              {lastSalesLoading && <p>{t("seleccionBobinas.lastSales.loading")}</p>}
              {!lastSalesLoading && lastSalesError && (
                <div className="modern-alert alert-error">{t("seleccionBobinas.lastSales.loadError")}</div>
              )}
              {!lastSalesLoading && !lastSalesError && (
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>{t("seleccionBobinas.lastSales.columnDate")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnItem")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnGramaje")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnName")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnWidth")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnDepth")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnQty")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnPrice")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnDiscount")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnAmount")}</th>
                        <th>{t("seleccionBobinas.lastSales.columnCustAccount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastSales.map((row, idx) => (
                        <tr key={`${getFieldLoose(row, "recId")}-${idx}`}>
                          <td>{formatDateOnly(getFieldLoose(row, "invoicedate"))}</td>
                          <td>{getFieldLoose(row, "itemid")}</td>
                          <td>{getFieldLoose(row, "gramajeitemid")}</td>
                          <td>{getFieldLoose(row, "name")}</td>
                          <td>{formatNumber(getFieldLoose(row, "width"))}</td>
                          <td>{formatNumber(getFieldLoose(row, "depth"))}</td>
                          <td>{formatNumber(getFieldLoose(row, "qty"))}</td>
                          <td>{formatNumber(getFieldLoose(row, "salesprice"))}</td>
                          <td>{formatNumber(getFieldLoose(row, "linedisc"))}</td>
                          <td>{formatNumber(getFieldLoose(row, "lineamount"))}</td>
                          {/* CustInvoiceTrans has no custaccount/invoiceaccount column of
                              its own in AX4; every row here already matches
                              custAccountForSales by construction (see findLastSales). */}
                          <td>{custAccountForSales}</td>
                        </tr>
                      ))}
                      {lastSales.length === 0 && (
                        <tr><td colSpan={11}>{t("seleccionBobinas.lastSales.noResults")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Zona inferior: seleccion de bobinas */}
      <div className="crud-form-container">
        <div className="crud-form-tabs-nav-scroll" role="tablist">
          <ul className="nav nav-tabs crud-form-tabs-nav">
            {tabButton(bottomTab === "general", () => setBottomTab("general"), "seleccionBobinas.bottomTabs.general")}
            {tabButton(bottomTab === "details", () => setBottomTab("details"), "seleccionBobinas.bottomTabs.details")}
            {tabButton(bottomTab === "legend", () => setBottomTab("legend"), "seleccionBobinas.bottomTabs.legend")}
          </ul>
        </div>
        <div style={{ padding: 12 }}>
          {(bottomTab === "general" || bottomTab === "details") && (
            <div style={{ marginBottom: 12 }}>
              <label>
                <input type="checkbox" checked={colorsDisabled} onChange={(e) => setColorsDisabled(e.target.checked)} />
                {" "}{t("seleccionBobinas.general.disableExtraColors")}
              </label>
            </div>
          )}
          <div style={{ maxHeight: 520, overflow: "auto" }}>
            {bottomTab === "general" && lines.map((line) => {
              const recId = getFieldLoose(line, "recId");
              const calc = lineComputations[recId] || { candidates: [], selections: [], contributionByRecId: {} };
              const selectionsByKey = new Map((calc.selections || []).map((s) => [selectionRowKey(s), s]));
              return (
                <div key={recId} style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 4 }}>
                    {getFieldLoose(line, "itemId")} - {getFieldLoose(line, "name")}
                    {" "}
                    <small style={{ color: "#6b7280" }}>
                      {t("seleccionBobinas.general.orderedVsSelected", {
                        ordered: formatNumber(getFieldLoose(line, "salesQty")),
                        selected: formatNumber(calc.totalKg),
                      })}
                    </small>
                  </h4>
                  {!recId && <p style={{ color: "#9ca3af" }}>{t("seleccionBobinas.general.lineNotSyncedYet")}</p>}
                  {recId && (
                    <div style={{ overflowX: "auto" }}>
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>{t("seleccionBobinas.general.columnType")}</th>
                            <th>{t("seleccionBobinas.general.columnItem")}</th>
                            <th>{t("seleccionBobinas.general.columnName")}</th>
                            <th>{t("seleccionBobinas.general.columnGramaje")}</th>
                            <th>{t("seleccionBobinas.general.columnWidth")}</th>
                            <th>{t("seleccionBobinas.general.columnDepth")}</th>
                            <th>{t("seleccionBobinas.general.columnRemainingQty")}</th>
                            <th>{t("seleccionBobinas.general.columnDate")}</th>
                            <th>{t("seleccionBobinas.general.columnBatchOrTrans")}</th>
                            <th>{t("seleccionBobinas.general.columnLocation")}</th>
                            <th>{t("seleccionBobinas.general.columnMargin")}</th>
                            <th>{t("seleccionBobinas.general.columnMerma")}</th>
                            <th>{t("seleccionBobinas.general.columnTransportCost")}</th>
                            <th>{t("seleccionBobinas.general.columnSelection")}</th>
                            <th>{t("seleccionBobinas.general.columnDelegation")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(calc.candidates || []).map((candidate) => {
                            const key = candidateKey(candidate);
                            const existingSelection = selectionsByKey.get(key);
                            const busyKey = `${recId}:${key}`;
                            return (
                              <CandidateRow
                                key={key}
                                candidate={candidate}
                                itemAttributes={itemAttrCache[candidate.itemId]}
                                existingSelection={existingSelection}
                                colorsDisabled={colorsDisabled}
                                canEdit={canEditSelection}
                                busy={busyKeys.has(busyKey)}
                                onToggleFlag={(cand, flag, checked) => handleToggleFlag(line, cand, flag, checked)}
                                onDelegacionSave={(sel, payload) => handleDelegacionSave(
                                  line, sel, payload, calc.contributionByRecId?.[sel?.recId],
                                )}
                              />
                            );
                          })}
                          {(calc.candidates || []).length === 0 && (
                            <tr><td colSpan={15}>{t("seleccionBobinas.general.noCandidates")}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {bottomTab === "details" && (
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>{t("seleccionBobinas.general.columnType")}</th>
                    <th>{t("seleccionBobinas.general.columnItem")}</th>
                    <th>{t("seleccionBobinas.general.columnRemainingQty")}</th>
                    <th>{t("seleccionBobinas.general.columnBatchOrTrans")}</th>
                    <th>{t("seleccionBobinas.general.columnSelection")}</th>
                    <th>{t("seleccionBobinas.general.columnDelegation")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const recId = getFieldLoose(line, "recId");
                    const calc = lineComputations[recId] || { candidates: [], selections: [] };
                    const selectionsByKey = new Map((calc.selections || []).map((s) => [selectionRowKey(s), s]));
                    return (calc.candidates || []).map((candidate) => (
                      <CandidateRow
                        key={`${recId}-${candidateKey(candidate)}`}
                        compact
                        candidate={candidate}
                        itemAttributes={itemAttrCache[candidate.itemId]}
                        existingSelection={selectionsByKey.get(candidateKey(candidate))}
                        colorsDisabled={colorsDisabled}
                        canEdit={canEditSelection}
                      />
                    ));
                  })}
                </tbody>
              </table>
            )}

            {bottomTab === "legend" && <LegendTab />}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(coverageConfirm)}
        onClose={() => setCoverageConfirm(null)}
        onConfirm={doMarkReviewed}
        title={t("seleccionBobinas.actions.coverageConfirmTitle")}
        message={`${t("seleccionBobinas.actions.coverageConfirmMessage")} ${(coverageConfirm || []).map((line) =>
          `${line.itemId}: ${formatNumber(line.selectedQty)}/${formatNumber(line.orderedQty)} kg (${formatNumber(line.coveragePercent)}% - ${line.status})`
        ).join("; ")}`}
        confirmText={t("seleccionBobinas.actions.coverageConfirmProceed")}
        cancelText={t("common.cancel")}
        type="warning"
      />
    </div>
  );
}
